/* src/components/WorkspaceMenu.jsx — always-visible workspace switcher (logo dropdown) */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useWorkspaceNames, workspaceDisplayName } from '../hooks/useWorkspaceNames';
import { usageApi } from '../utils/api';
import CreateClientWorkspaceControl from './CreateClientWorkspaceControl';

export default function WorkspaceMenu() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    selectedWorkspaceId,
    availableWorkspaces,
    workspaceDetails,
    switchWorkspace,
  } = useWorkspace();

  const workspaceNames = useWorkspaceNames(availableWorkspaces, currentUser?.uid, workspaceDetails);
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !selectedWorkspaceId) return undefined;
    let cancelled = false;
    usageApi.getUsage(selectedWorkspaceId)
      .then((data) => {
        if (!cancelled && data?.success) setUsage(data);
      })
      .catch((error) => {
        console.error('[WORKSPACE MENU] getUsage failed:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedWorkspaceId]);

  const activeName = workspaceDisplayName(selectedWorkspaceId, currentUser?.uid, workspaceNames);

  async function handleSwitch(workspaceId) {
    switchWorkspace(workspaceId);
    setOpen(false);
    await new Promise((resolve) => setTimeout(resolve, 100));
    navigate('/form-builder');
  }

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: 0,
          margin: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: "'Archivo', sans-serif",
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Workspace menu"
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: '19px',
            letterSpacing: '-0.02em',
            fontStretch: '115%',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span>
            gocta<span style={{ color: 'var(--accent-ink)' }}>.ai</span>
          </span>
          <span
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '10px',
              color: 'var(--dim)',
              fontWeight: 400,
            }}
          >
            {open ? '▲' : '▼'}
          </span>
        </div>
        <div
          style={{
            marginTop: '6px',
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {activeName}
        </div>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Workspaces"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: 'var(--surface)',
            border: '1px solid var(--hairline-3)',
            borderRadius: '8px',
            padding: '6px',
            zIndex: 1002,
            maxHeight: '280px',
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}
        >
          <div
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '10px',
              color: 'var(--dim)',
              letterSpacing: '0.06em',
              padding: '6px 8px 4px',
            }}
          >
            WORKSPACE
          </div>

          {(availableWorkspaces || []).map((workspaceId) => {
            const isSelected = workspaceId === selectedWorkspaceId;
            const detail = (workspaceDetails || []).find((d) => d.id === workspaceId);
            const isOwner = detail ? detail.role === 'owner' : workspaceId === currentUser?.uid;
            const label = workspaceDisplayName(workspaceId, currentUser?.uid, workspaceNames);
            return (
              <button
                key={workspaceId}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSwitch(workspaceId)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  padding: '8px 10px',
                  background: isSelected
                    ? 'color-mix(in srgb, var(--accent) 28%, transparent)'
                    : 'transparent',
                  color: 'var(--ink)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: isSelected ? 600 : 400,
                  textAlign: 'left',
                  fontFamily: "'Archivo', sans-serif",
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isSelected ? '✓ ' : ''}{label}
                </span>
                <span className="inbox-chip" style={{ flexShrink: 0, fontSize: '10px' }}>
                  {isOwner ? 'Owner' : 'Member'}
                </span>
              </button>
            );
          })}

          <div style={{ borderTop: '1px solid var(--hairline-2)', margin: '6px 0' }} />

          <CreateClientWorkspaceControl usage={usage} />

          <Link
            to="/profile#workspaces"
            onClick={() => setOpen(false)}
            style={{
              display: 'block',
              padding: '8px 10px',
              fontSize: '13px',
              color: 'var(--muted)',
              textDecoration: 'none',
              fontFamily: "'Archivo', sans-serif",
            }}
          >
            Manage workspaces →
          </Link>
        </div>
      )}
    </div>
  );
}
